import { http } from '@lib/api';
import type { ListPage } from './list-envelope';
import type { components, paths } from '@/types/api.generated';
import { toApiListParams, type ListRequestParams } from './list-request';
import { AUDIT_LOG_FILTERS as AUDIT_FILTERS, RESOURCE_AUDIT_FILTERS } from './filters.generated';

/**
 * Operator audit-ledger row — `GET /api/audit-log` (#944, M6.2). This is
 * the request-access ledger: one row per privileged HTTP call
 * (method / route_path / permission / outcome / trace_id). It is NOT the
 * archived `/admin/audit-logs` domain-change face (action / details /
 * resourceType), which was removed with the backend rebuild (#947).
 * Consumed from the generated OpenAPI types so a backend contract change
 * surfaces at typecheck.
 */
export type AuditLog = components['schemas']['AuditLog'];

/** Generated wire contract for the resource-level data-change ledger. */
export type ResourceAuditLog = components['schemas']['ResourceAuditLog'];
/**
 * Since #1980 every query key is optional, so openapi-typescript now emits the
 * whole `query` bag as `{…} | undefined`. `NonNullable` keeps this alias the
 * OBJECT type it has always been (all members individually optional) — without
 * it, indexing a member off the alias is a TS2339.
 */
export type ResourceAuditQuery = NonNullable<
  paths['/api/resource-audit']['get']['parameters']['query']
>;
/**
 * The four ledger-bearing resource types. `NonNullable` is load-bearing since
 * #1980 made `resource_type` an OPTIONAL query param: without it the alias
 * widens to include `undefined`, and widening is the direction the compiler
 * mostly tolerates — the "must be one of four" guarantee erodes at every
 * consumer (`AuditTimeline`'s required `resourceType` prop,
 * `resource-kit-contracts`' `auditResourceType`) without any of them being
 * the place that names the rule.
 */
export type ResourceAuditResourceType = NonNullable<ResourceAuditQuery['resource_type']>;

/**
 * Compile-time negative assertion pinning the narrowing above. If the
 * `NonNullable<>` is ever dropped, `undefined extends …` flips to `true`, the
 * conditional resolves to `false`, and `false` does not satisfy the `extends
 * true` constraint — `tsc --noEmit` fails with TS2344 rather than letting the
 * widening through in silence. Type-only: nothing is emitted.
 */
type AssertTrue<T extends true> = T;
export type ResourceAuditResourceTypeExcludesUndefined = AssertTrue<
  undefined extends ResourceAuditResourceType ? false : true
>;
export type ResourceAuditAction = ResourceAuditLog['action'];

export interface ResourceAuditFieldChange {
  field: string;
  oldValue: unknown;
  newValue: unknown;
}

/** View model consumed by AuditTimeline. */
export interface ResourceAuditTimelineItem {
  id: string;
  /**
   * Which resource the row is about, and which row of it (#1981).
   *
   * The per-record history tab never needed either — it asks for one
   * `(resource_type, resource_id)` pair and already knows the answer. The
   * GLOBAL change-history list spans all four types, so every consumer
   * downstream of the map (the field index the diff renders through, the link
   * the 资源 column carries) has to read them off the entry itself.
   */
  resourceType: ResourceAuditResourceType;
  resourceId: string;
  action: ResourceAuditAction;
  actorType: ResourceAuditLog['actor_type'];
  actorId: string | null;
  actorLabel: string | null;
  source: ResourceAuditLog['source'];
  eventId: string | null;
  createdAt: string;
  changes: ResourceAuditFieldChange[];
  newValues: Record<string, unknown> | null;
  oldValues: Record<string, unknown> | null;
}

function isObjectRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function snapshotFrom(
  parsed: unknown,
  key: 'new_values' | 'old_values',
  rowId: string,
): Record<string, unknown> | null {
  if (!isObjectRecord(parsed) || !(key in parsed)) {
    throw new Error(`Resource audit row ${rowId} has invalid ${key} JSON`);
  }
  const snapshot = parsed[key];
  if (snapshot === null || isObjectRecord(snapshot)) return snapshot;
  throw new Error(`Resource audit row ${rowId} has invalid ${key} snapshot`);
}

function fieldChangesFrom(parsed: unknown, rowId: string): ResourceAuditFieldChange[] {
  if (!Array.isArray(parsed)) {
    throw new Error(`Resource audit row ${rowId} has invalid field-change JSON`);
  }
  return parsed.map((change) => {
    if (!isObjectRecord(change) || typeof change.field !== 'string') {
      throw new Error(`Resource audit row ${rowId} has an invalid field change`);
    }
    return {
      field: change.field,
      oldValue: change.old,
      newValue: change.new,
    };
  });
}

/** Adapt the generated snake_case row and JSON payload to timeline semantics. */
export function mapResourceAuditLog(row: ResourceAuditLog): ResourceAuditTimelineItem {
  const parsed: unknown = JSON.parse(row.changes);
  const isCreate = row.action === 'create';
  const isDelete = row.action === 'delete';

  return {
    id: row.id,
    resourceType: row.resource_type,
    resourceId: row.resource_id,
    action: row.action,
    actorType: row.actor_type,
    actorId: row.actor_id,
    actorLabel: row.actor_label,
    source: row.source,
    eventId: row.event_id,
    createdAt: row.created_at,
    changes: isCreate || isDelete ? [] : fieldChangesFrom(parsed, row.id),
    newValues: isCreate ? snapshotFrom(parsed, 'new_values', row.id) : null,
    oldValues: isDelete ? snapshotFrom(parsed, 'old_values', row.id) : null,
  };
}

/**
 * Closed set of ledger outcomes, mirroring the backend `AUDIT_OUTCOME`
 * (`backend/src/platform/audit/store/schema.ts`): a 2xx writes `ok`, a
 * 4xx/5xx/throw writes `error`. The wire schema types `outcome` as a bare
 * `string`, so the UI narrows against this map to colour the badge; an
 * out-of-set value fails loud via the shared `[unknown:*]` marker rather than
 * silently rendering a plausible badge.
 */
export const AUDIT_OUTCOME = {
  OK: 'ok',
  ERROR: 'error',
} as const;
export type AuditOutcome = (typeof AUDIT_OUTCOME)[keyof typeof AUDIT_OUTCOME];

/** Backend default page size for the ledger (audit-log-routes `defaultSize`). */
export const DEFAULT_PAGE_SIZE = 20;

// The `/api/audit-log` filter whitelist is codegen from the backend
// `auditLogListConfig.filterable` via the OpenAPI `x-filterable` extension
// (issue #1407); edit the backend config, not this file. `action` pins to
// the route_path column, `actor` fans out to actor_id / actor_label in the
// store, `created_at` is a range. The ledger has NO free-text search column
// (backend rejects `search` with 400, 裁决 9). Re-exported under the
// `AUDIT_FILTERS` name so the `@services/audit` import path is unchanged.
export { AUDIT_FILTERS };

export const auditAPI = {
  /**
   * Fetch a page of audit-ledger rows. `http.get` already unwraps the
   * outer `{ data }` envelope, so this returns the inner
   * `{ items, total, page, size }` list shape verbatim (ADR-0006 §5).
   */
  async list(params: ListRequestParams = {}): Promise<ListPage<AuditLog>> {
    // The ledger has NO free-text column and rejects the reserved `search`
    // param with 400 (裁决 9). Strip it here so no caller can smuggle a
    // `search` into a request the backend would reject; text-shaped intent
    // must go through the whitelisted `action` (route_path) filter instead.
    const { search: _search, ...rest } = params;
    return http.get<ListPage<AuditLog>>('/api/audit-log', {
      params: toApiListParams(rest, AUDIT_FILTERS),
    });
  },
};

export const resourceAuditAPI = {
  /**
   * Fetch the data-change ledger. Every filter in the generated query type is
   * optional since #1980: pass `resource_type` + `resource_id` for one
   * resource's history, or omit them for the global cross-resource page. The
   * type also carries the endpoint's list-kernel pagination and its
   * actor/action/source/created_at filters.
   */
  async list(params: ResourceAuditQuery): Promise<ListPage<ResourceAuditTimelineItem>> {
    const page = await http.get<ListPage<ResourceAuditLog>>('/api/resource-audit', { params });
    return { ...page, items: page.items.map(mapResourceAuditLog) };
  },

  /**
   * The same endpoint through the shared list-kernel params, returning the WIRE
   * rows unmapped (#1981).
   *
   * Two callers with genuinely different needs, not a duplicate: `list` above
   * serves the per-record timeline, which asks in the endpoint's own query
   * shape and wants only the view model. The global list page arrives with
   * `ListRequestParams` (page / size / sort / serialized filters) and keeps the
   * snake_case wire row as its record, because every column key it declares has
   * to equal the backend's filter and sort key — `created_at`, not `createdAt`.
   * The bundle attaches the mapped entry itself.
   */
  async listRaw(params: ListRequestParams = {}): Promise<ListPage<ResourceAuditLog>> {
    // The ledger declares no searchable column (`RESOURCE_AUDIT_SEARCH` is
    // empty), and the kernel 400s a `search` param on such a route — the same
    // guard `auditAPI.list` carries, for the same reason.
    const { search: _search, ...rest } = params;
    return http.get<ListPage<ResourceAuditLog>>('/api/resource-audit', {
      params: toApiListParams(rest, RESOURCE_AUDIT_FILTERS),
    });
  },
};
